import { useEffect, useMemo } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import reportFrontendError from '@/lib/report-frontend-error';
import reloadOnStaleChunk from '@/lib/reload-on-stale-chunk';

const isDev = import.meta.env.DEV;

const generateErrorId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`.toUpperCase();

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const isRouteResponse = isRouteErrorResponse(error);

  const message = isRouteResponse
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : String(error);

  const stack = error instanceof Error ? error.stack : undefined;

  const errorId = useMemo(() => generateErrorId(), []);

  useEffect(() => {
    if (isRouteResponse) {
      return;
    }

    // React Router's lazy() catches chunk-load rejections internally and
    // routes them here rather than raising a window 'error'/'unhandledrejection'
    // event, so this boundary needs its own stale-chunk reload check.
    if (reloadOnStaleChunk(message)) {
      return;
    }

    reportFrontendError({ errorId, message, stack });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Card className="w-[400px] shadow-lg border border-gray-700">
        <CardHeader className="flex flex-col items-center text-center">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <CardTitle className="text-2xl font-bold mt-4">
            Something went wrong
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-gray-400 mb-4">
            The page could not be displayed. If this keeps happening, please
            contact support with reference {errorId}.
          </p>
          {isDev && (
            <pre className="text-left text-xs whitespace-pre-wrap break-words text-red-400 mb-4 max-h-64 overflow-auto">
              {message}
              {stack ? `\n${stack}` : ''}
            </pre>
          )}
          <Button className="w-full" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
